import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ClockIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}
function FAQItem({
  question,
  answer
}: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm" data-id="element-139">
      <button className="flex justify-between items-center w-full text-left p-5 bg-white hover:bg-gray-50 transition-colors focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-140">
        <h3 className="text-xl font-medium text-gray-800" data-id="element-141">{question}</h3>
        <span className="text-purple-600" data-id="element-142">
          {isOpen ? <ChevronUpIcon size={24} data-id="element-143" /> : <ChevronDownIcon size={24} data-id="element-144" />}
        </span>
      </button>
      {isOpen && <div className="p-5 bg-white border-t border-gray-200" data-id="element-145">
          <div className="text-lg text-gray-700" data-id="element-146">{answer}</div>
        </div>}
    </div>;
}
export function FAQ() {
  return <div className="w-full my-10" data-id="element-147">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center" data-id="element-148">
        Frequently Asked Questions
      </h2>
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-center justify-center" data-id="element-149">
        <ClockIcon className="w-5 h-5 text-red-600 mr-2" data-id="element-150" />
        <p className="text-red-600 font-medium" data-id="element-151">
          Enrollment open for a limited time - Join now to secure your spot
        </p>
      </div>
      <div className="space-y-4" data-id="element-152">
        <FAQItem question="What exactly is Unfreeze?" answer={<p className="mb-4" data-id="element-154">
              Unfreeze is a 4-week, creative, body-based nervous system reset
              designed to gently thaw emotional numbness and trauma-related
              shutdown. It uses expressive practices like movement, sound, and
              symbolic art to help restore safety, sensation, and
              self-connection—without forcing emotion or needing to "go deep."
            </p>} data-id="element-153" />
        <FAQItem question="Do I need to be creative or artistic to do this?" answer={<p className="mb-4" data-id="element-156">
              Not at all. This program isn't about being artistic—it's about
              using creativity as a tool for nervous system repair. There's no
              right or wrong way to do any exercise. You can scribble, hum, or
              move in your own rhythm. The healing is in the doing, not in the
              result.
            </p>} data-id="element-155" />
        <FAQItem question="What if I don't feel anything during the exercises?" answer={<p className="mb-4" data-id="element-158">
              That's completely okay. This program is designed for people who
              feel disconnected or shut down. You don't have to feel anything
              dramatic. The simple act of showing up—making a mark, moving your
              hand, exploring a sound—is enough to start the thawing process.
              Subtle shifts are still progress.
            </p>} data-id="element-157" />
        <FAQItem question="Is this therapy? Should I stop seeing my therapist?" answer={<p className="mb-4" data-id="element-160">
              Unfreeze is not a replacement for therapy or medical treatment.
              It's a self-guided, body-first support tool that can complement
              your existing care. Many participants use it alongside therapy to
              deepen their healing experience.
            </p>} data-id="element-159" />
        <FAQItem question="What do I get when I join?" answer={<>
              <p className="mb-4" data-id="element-162">You'll get:</p>
              <ul className="list-disc pl-6 mb-4" data-id="element-163">
                <li className="mb-2" data-id="element-164">4 trauma-informed video practices</li>
                <li className="mb-2" data-id="element-165">
                  A beautifully designed printable workbook (30+ pages)
                </li>
                <li className="mb-2" data-id="element-166">Access to a live group healing call</li>
                <li className="mb-2" data-id="element-167">
                  Lifetime access to everything, so you can return any time you
                  need
                </li>
              </ul>
            </>} data-id="element-161" />
        <FAQItem question="How long will I have access to the program?" answer={<p className="mb-4" data-id="element-169">
              Forever. Once you join, you'll have lifetime access to the full
              Unfreeze program, including any future updates or bonus content.
            </p>} data-id="element-168" />
        <FAQItem question="How is this different from other nervous system programs?" answer={<p className="mb-4" data-id="element-171">
              Most programs focus on calming down, regulating anxiety, or
              managing symptoms. Unfreeze is different—it's designed
              specifically for freeze states like numbness, emotional shutdown,
              and dissociation. It doesn't ask you to feel more. It helps your
              system feel safe enough to begin feeling again—gently and at your
              own pace.
            </p>} data-id="element-170" />
        <FAQItem question="What if I want access to more programs like this?" answer={<p className="mb-4" data-id="element-173">
              You can upgrade to Neuro Monthly+ to get instant access to all of
              our themed monthly journeys—like Healing Chronic Fear, Coping With
              Grief, Dissociation & Reconnection, and more—plus every live
              healing call and all future programs. It's just $90/year for full
              access.
            </p>} data-id="element-172" />
        <FAQItem question="Is there a guarantee?" answer={<p className="mb-4" data-id="element-175">
              Yes. We offer a 30-day gentle guarantee. If the program doesn't
              resonate with you for any reason, just email us within 30 days and
              we'll issue a full refund—no pressure, no guilt, no hoops.
            </p>} data-id="element-174" />
        <FAQItem question="How long will enrollment be open?" answer={<p className="mb-4" data-id="element-177">
              Enrollment for this transformative program is only open for a
              limited time. We can't guarantee how long this opportunity will
              last, so we recommend joining now if you're interested.
            </p>} data-id="element-176" />
      </div>
    </div>;
}